import React from 'react'
import { useTranslation } from 'react-i18next'
import { Checkbox } from 'examkrackers-components'
import { QuizConceptSummary, QuizResultRow } from './quiz.types'
import ConfettiBurst from './ConfettiBurst'
import { formatQuizzesDueTodayCount } from 'modules/quizDiagnostics/utils/formatQuizzesDueTodayCount'
import {
  ConceptsContainer,
  QuizMeAgainButton,
  PercentageLine,
  ResultBlock,
  ResultLabel,
  ResultsHeading,
  ResultsSummary,
  ResultQuestion,
  ResultState,
  ScoreLine,
  StickyFooter,
  FooterButtons,
  ImageSize,
  WeakConceptsList,
  ResultLabelLabel,
  NextQuizButton,
  NextQuizButtonWrapper,
  NextQuizDueBadge,
  TakeQuizButton,
  ResultBtnLabel,
  OutputWrapper,
  SeeQuizDiagnosticsLink,
  IncludeInAlgorithmRow,
  IncludeInAlgorithmLabel,
  CongratulationsPhrase,
  CongratulationsText,
  FeedbackText,
  ScheduleText,
  ScheduleHighlight
} from './QuizModal.styles'

interface QuizResultsViewProps {
  scoreLabel: string
  percentageText: string
  explanationTitle: string
  correctAnswerLabel: string
  yourAnswerLabel: string
  explanationLabel: string
  correctText: string
  incorrectText: string
  rows: QuizResultRow[]
  conceptSummary: QuizConceptSummary
  finalScoreLabel: string
  strengthsLabel: string
  weaknessesLabel: string
  quizMeAgainLabel: string
  nextQuizLabel: string
  takeMeAgainQuizLabel: string
  onGenerateNewQuiz: () => void
  onStartNextInQueue: () => void
  hasNextInQueue: boolean
  courseQuizzesDueToday: number
  includeInAlgorithmChecked: boolean
  includeInAlgorithmSectionLabel: string
  includeInAlgorithmDisabled?: boolean
  onIncludeInAlgorithmChange: () => void
  finalPercentage: number
  feedbackLabel: string
  nextQuizSchedulePrefix: string
  nextQuizScheduleHighlight: string
  seeQuizDiagnosticsLabel?: string
  onSeeQuizDiagnostics?: () => void
}

const formatStrongConcepts = (concepts: string[]) => {
  if (concepts?.length === 0) return ''
  if (concepts?.length === 1) return concepts[0]
  if (concepts?.length === 2) return `${concepts[0]} and ${concepts[1]}`
  return `${concepts?.slice(0, -1).join(', ')}, and ${
    concepts[concepts?.length - 1]
  }`
}

const QuizResultsView: React.FC<QuizResultsViewProps> = ({
  scoreLabel,
  percentageText,
  explanationTitle,
  correctAnswerLabel,
  yourAnswerLabel,
  explanationLabel,
  correctText,
  incorrectText,
  rows,
  conceptSummary,
  finalScoreLabel,
  quizMeAgainLabel,
  nextQuizLabel,
  takeMeAgainQuizLabel,
  onGenerateNewQuiz,
  onStartNextInQueue,
  hasNextInQueue,
  courseQuizzesDueToday,
  includeInAlgorithmChecked,
  includeInAlgorithmSectionLabel,
  includeInAlgorithmDisabled = false,
  onIncludeInAlgorithmChange,
  finalPercentage,
  feedbackLabel,
  nextQuizSchedulePrefix,
  nextQuizScheduleHighlight,
  seeQuizDiagnosticsLabel,
  onSeeQuizDiagnostics
}) => {
  const { t } = useTranslation()
  const finalPercentageCheck = Number(finalPercentage) < 50
  const isPerfectScore = Number(finalPercentage) === 100
  // Show whenever the srq-red-dots API (or store) reports a due count.
  const showDueTodayBadge = courseQuizzesDueToday > 0
  const dueTodayCountLabel = formatQuizzesDueTodayCount(courseQuizzesDueToday)
  const nextQuizAriaLabel = showDueTodayBadge
    ? `${nextQuizLabel}, ${dueTodayCountLabel} quizzes due today`
    : nextQuizLabel
  const takeQuizAgainAriaLabel = showDueTodayBadge
    ? `${takeMeAgainQuizLabel}, ${dueTodayCountLabel} quizzes due today`
    : takeMeAgainQuizLabel
  const includeInAlgorithmLabel = t('aiSpaceRepetition.includeInAlgorithm', {
    section: includeInAlgorithmSectionLabel
  })

  const handleIncludeInAlgorithmLabelKeyDown = (
    event: React.KeyboardEvent<HTMLSpanElement>
  ) => {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return
    }

    event.preventDefault()
    if (includeInAlgorithmDisabled) {
      return
    }
    onIncludeInAlgorithmChange()
  }

  const renderFeedbackMessage = () => {
    if (!isPerfectScore) {
      return feedbackLabel
    }

    return (
      <CongratulationsPhrase>
        <ConfettiBurst />
        <CongratulationsText>{feedbackLabel}</CongratulationsText>
      </CongratulationsPhrase>
    )
  }

  return (
    <div>
      <ResultsSummary>
        <ScoreLine>{scoreLabel}</ScoreLine>
        <PercentageLine>{percentageText}</PercentageLine>
        <FeedbackText>{renderFeedbackMessage()}</FeedbackText>
        {!finalPercentageCheck &&
          nextQuizSchedulePrefix &&
          nextQuizScheduleHighlight && (
          <ScheduleText>
            {nextQuizSchedulePrefix}{' '}
            <ScheduleHighlight>{nextQuizScheduleHighlight}</ScheduleHighlight>
          </ScheduleText>
        )}
      </ResultsSummary>
      <ResultsHeading>{explanationTitle}</ResultsHeading>

      {rows?.map((row, index) => (
        <ResultBlock key={row?.questionId}>
          <ResultQuestion>
            {index + 1}. {row?.prompt}
          </ResultQuestion>
          <ResultLabel>
            {correctAnswerLabel}: {row?.correctAnswer}
          </ResultLabel>
          <ResultLabel>
            {yourAnswerLabel}: {row?.userAnswer}
          </ResultLabel>
          <ResultLabel>
            <ResultState isCorrect={row?.isCorrect}>
              {row?.isCorrect ? (
                <>
                  <ImageSize>✅</ImageSize> {correctText}
                </>
              ) : (
                <>
                  <ImageSize>❌</ImageSize> {incorrectText}
                </>
              )}
            </ResultState>
          </ResultLabel>
          <ResultLabel>
            {explanationLabel}: {row?.explanation}
          </ResultLabel>
        </ResultBlock>
      ))}

      <ConceptsContainer>
        <ResultLabelLabel>{finalScoreLabel}</ResultLabelLabel>
        {conceptSummary?.strongConcepts?.length > 0 && (
          <ResultLabel>
            You have a solid grasp of{' '}
            {formatStrongConcepts(conceptSummary?.strongConcepts)} 👍
          </ResultLabel>
        )}
        {conceptSummary?.weakConcepts?.length > 0 && (
          <>
            <ResultLabel>The main areas to review are:</ResultLabel>
            <WeakConceptsList>
              {conceptSummary?.weakConcepts?.map(concept => (
                <li key={concept}>{concept}</li>
              ))}
            </WeakConceptsList>
          </>
        )}
      </ConceptsContainer>

      <StickyFooter>
        <FooterButtons finalPercentageCheck={finalPercentageCheck}>
          {finalPercentageCheck ? (
            <OutputWrapper>
              <ResultBtnLabel>You can do better.</ResultBtnLabel>
              <TakeQuizButton
                color='orange'
                onClick={onGenerateNewQuiz}
                aria-label={takeQuizAgainAriaLabel}
              >
                {takeMeAgainQuizLabel}
                {showDueTodayBadge ? (
                  <NextQuizDueBadge aria-hidden='true'>
                    {dueTodayCountLabel}
                  </NextQuizDueBadge>
                ) : null}
              </TakeQuizButton>
            </OutputWrapper>
          ) : (
            <>
              <OutputWrapper>
                <ResultBtnLabel>Keep quizzing in this section.</ResultBtnLabel>
                <QuizMeAgainButton
                  color='secondary'
                  onClick={onGenerateNewQuiz}
                >
                  {quizMeAgainLabel}
                </QuizMeAgainButton>
              </OutputWrapper>
              <OutputWrapper>
                <ResultBtnLabel>
                  {!hasNextInQueue
                    ? 'Your daily quizzes are complete.'
                    : 'Work on daily quiz list.'}
                </ResultBtnLabel>
                <NextQuizButtonWrapper>
                  <NextQuizButton
                    color='orange'
                    disabled={!hasNextInQueue}
                    onClick={onStartNextInQueue}
                    aria-label={nextQuizAriaLabel}
                  >
                    {nextQuizLabel}
                    {showDueTodayBadge ? (
                      <NextQuizDueBadge aria-hidden='true'>
                        {dueTodayCountLabel}
                      </NextQuizDueBadge>
                    ) : null}
                  </NextQuizButton>
                </NextQuizButtonWrapper>
              </OutputWrapper>
            </>
          )}
        </FooterButtons>
        <IncludeInAlgorithmRow>
          <Checkbox
            name='include-section-in-srq-algorithm'
            isSelected={includeInAlgorithmChecked}
            onChange={onIncludeInAlgorithmChange}
            disabled={includeInAlgorithmDisabled}
          />
          <IncludeInAlgorithmLabel
            role='button'
            tabIndex={includeInAlgorithmDisabled ? -1 : 0}
            aria-label={includeInAlgorithmLabel}
            onClick={() => {
              if (includeInAlgorithmDisabled) {
                return
              }
              onIncludeInAlgorithmChange()
            }}
            onKeyDown={handleIncludeInAlgorithmLabelKeyDown}
          >
            {includeInAlgorithmLabel}
          </IncludeInAlgorithmLabel>
        </IncludeInAlgorithmRow>
        {seeQuizDiagnosticsLabel && onSeeQuizDiagnostics ? (
          <SeeQuizDiagnosticsLink type='button' onClick={onSeeQuizDiagnostics}>
            {seeQuizDiagnosticsLabel}
          </SeeQuizDiagnosticsLink>
        ) : null}
      </StickyFooter>
    </div>
  )
}

export default QuizResultsView
